// ClientComplianceScorePanel — blueprint module #4 Compliance Center.
//
// Every client gets a compliance score computed from the audit packet
// the server already assembles. Score = weighted % across 8 required
// items:
//
//   ✓ Consent recorded
//   ✓ Permission to text
//   ✓ Permission to email
//   ✓ Marketplace disclosures completed
//   ✓ SOA completed (Medicare only)
//   ✓ Call recorded
//   ✓ Signature captured
//   ✓ Documents uploaded
//
// Renders a big 0-100 score, a "safe to submit" badge, and the
// checkmark grid. When items are missing, the panel is the source
// of truth for WHY a submission would fail.
//
// Data: reads /api/audit/packet?client_id=X (the same endpoint
// ClientFormsPanel uses, so nothing needs a new server route).

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path) {
  let jwt = null;
  jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
  });
  return res.json();
}

async function apiPost(path, body) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
    body: JSON.stringify(body),
  });
  return res.json();
}

// The 8 blueprint compliance items + how to detect each from an audit
// packet. Each key is a function that returns { done, detail } where
// `detail` is a short human explanation. Keep the detection LENIENT —
// missing signal counts as "not done", not "unknown", so the score is
// stable when the schema is partial.
const CHECKS = [
  {
    key: 'consent',
    label: 'Consent recorded',
    detect: (pkt) => {
      const forms = pkt.forms || [];
      const c = forms.find(f => /consent|hipaa|acknowledg/i.test(f.form_type || ''));
      return { done: !!c && c.status === 'completed', detail: c ? c.status : 'no consent form on file' };
    },
  },
  {
    key: 'sms_permission',
    label: 'Permission to text',
    detect: (pkt) => {
      const evt = (pkt.events || []).find(e => /sms.*consent|consent.*sms|permission.*text/i.test(e.event_type || '') || /sms/i.test((e.payload && JSON.stringify(e.payload)) || ''));
      return { done: !!evt, detail: evt ? 'captured' : 'not captured' };
    },
  },
  {
    key: 'email_permission',
    label: 'Permission to email',
    detect: (pkt) => {
      const evt = (pkt.events || []).find(e => /email.*consent|consent.*email/i.test(e.event_type || ''));
      return { done: !!evt, detail: evt ? 'captured' : 'not captured' };
    },
  },
  {
    key: 'marketplace_disc',
    label: 'Marketplace disclosures',
    detect: (pkt) => {
      const checklists = pkt.checklists || [];
      const d = checklists.find(c => /marketplace|disclosure|healthcare\.gov/i.test(c.item_key || c.label || ''));
      return { done: !!d && !!d.checked_at, detail: d?.checked_at ? 'checked' : 'not checked' };
    },
  },
  {
    key: 'soa',
    label: 'SOA (Medicare only)',
    detect: (pkt) => {
      const forms = pkt.forms || [];
      const soa = forms.find(f => /soa/i.test(f.form_type || ''));
      // If client isn't Medicare-eligible, treat as N/A (done=true, na=true).
      const isMedicare = (pkt.client?.type || '').toLowerCase().includes('medicare');
      if (!isMedicare && !soa) return { done: true, na: true, detail: 'not required (non-Medicare client)' };
      return { done: !!soa && soa.status === 'completed', detail: soa ? soa.status : 'no SOA on file' };
    },
  },
  {
    key: 'call_recorded',
    label: 'Call recorded',
    detect: (pkt) => {
      const evt = (pkt.events || []).find(e => /call.*record|recording/i.test(e.event_type || ''));
      return { done: !!evt, detail: evt ? 'recording stored' : 'no recording on file' };
    },
  },
  {
    key: 'signature',
    label: 'Signature captured',
    detect: (pkt) => {
      const signed = (pkt.forms || []).some(f => (f.signatures?.client || f.signatures?.agent));
      return { done: signed, detail: signed ? 'on file' : 'no signature captured' };
    },
  },
  {
    key: 'documents',
    label: 'Documents uploaded',
    detect: (pkt) => {
      // Loose signal — any doc event OR a form with a certificate_url.
      const docEvent = (pkt.events || []).find(e => /doc|upload|file/i.test(e.event_type || ''));
      const certForm = (pkt.forms || []).find(f => !!f.certificate_url);
      const done = !!docEvent || !!certForm;
      return { done, detail: done ? 'at least one document/cert on file' : 'no documents uploaded' };
    },
  },
];

// ACA Compliance Packet — the definitive "is this client OK to sign / enroll?"
// summary. Shows the two CMS-required ACA items (permission to contact + the
// signed ACA Consent & Eligibility Review form) with live status, a one-click
// "Send what's missing" (reuses the tested consent-request + soa-v2/send
// endpoints), and a toggle to auto-queue the form the moment a client is
// enrolled. Real status only — never claims compliant when it isn't.
function AcaCompliancePacket({ clientId }) {
  const [pkt, setPkt] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    if (!clientId) return;
    let cancel = false;
    (async () => {
      try {
        const j = await apiFetch(`/api/compliance/aca-packet?client_id=${encodeURIComponent(clientId)}`);
        if (!cancel && j?.ok) setPkt(j);
      } catch { /* leave null → panel simply doesn't show */ }
    })();
    return () => { cancel = true; };
  }, [clientId, tick]);

  if (!pkt) return null;
  const c = pkt.client || {};

  const sendMissing = async () => {
    setBusy(true); setToast(null);
    const results = [];
    try {
      for (const item of pkt.items.filter((i) => !i.done)) {
        if (item.key === 'permission') {
          const channel = c.phone ? 'sms' : (c.email ? 'email' : null);
          if (!channel) { results.push('no phone/email for consent request'); continue; }
          const r = await apiPost('/api/compliance/consent-request', { client_id: clientId, channel });
          results.push(r?.ok ? `consent request ${channel === 'sms' ? 'texted' : 'emailed'}` : (r?.reason || r?.error || 'consent request failed'));
        } else if (item.key === 'aca_consent') {
          if (!c.phone && !c.email) { results.push('no phone/email to send the form'); continue; }
          const r = await apiPost('/api/soa-v2/send', {
            form_type: 'aca_consent', beneficiary_id: clientId, beneficiary_name: c.name || 'Client',
            beneficiary_phone: c.phone || null, beneficiary_email: c.email || null,
          });
          results.push(r?.ok || r?.soa_id ? 'ACA consent form sent for signature' : (r?.error || 'form send failed'));
        }
      }
      setToast(results.join(' · ') || 'Nothing to send.');
      setTick((t) => t + 1);
    } catch (e) { setToast(e.message); } finally { setBusy(false); setTimeout(() => setToast(null), 6000); }
  };

  const toggleAuto = async () => {
    setBusy(true);
    try {
      const j = await apiPost('/api/compliance/aca-packet', { client_id: clientId, action: 'set_auto_dispatch', enabled: !pkt.auto_dispatch_enabled });
      if (j?.ok) setPkt((p) => ({ ...p, auto_dispatch_enabled: j.auto_dispatch_enabled }));
    } catch { /* */ } finally { setBusy(false); }
  };

  const statusPill = (item) => {
    if (item.done) return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">✓ On file</span>;
    if (item.status === 'not_sent' || item.status === 'missing') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Missing</span>;
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Awaiting signature</span>;
  };

  return (
    <div className={`rounded-xl border-2 px-4 py-3 ${pkt.compliant ? 'border-emerald-300 bg-emerald-50/60' : 'border-amber-300 bg-amber-50/50'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">ACA compliance packet</div>
        {pkt.compliant
          ? <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-emerald-600 text-white">✓ Fully compliant</span>
          : <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-amber-600 text-white">{pkt.missing_count} item{pkt.missing_count === 1 ? '' : 's'} needed</span>}
      </div>
      <div className="mt-2 space-y-1.5">
        {pkt.items.map((item) => (
          <div key={item.key} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-slate-800">{item.label}</div>
              <div className="text-[11px] text-slate-500">{item.detail}</div>
            </div>
            <div className="flex-none pt-0.5">{statusPill(item)}</div>
          </div>
        ))}
      </div>
      {!pkt.compliant && (
        <button onClick={sendMissing} disabled={busy}
          className="mt-2.5 text-[12px] font-bold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50">
          {busy ? 'Sending…' : 'Send what’s missing'}
        </button>
      )}
      <label className="mt-2.5 flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
        <input type="checkbox" checked={!!pkt.auto_dispatch_enabled} onChange={toggleAuto} disabled={busy}/>
        Auto-send the ACA consent form to enrolled clients who are missing it
      </label>
      {toast && <div className="mt-1.5 text-[11px] text-slate-700">{toast}</div>}
    </div>
  );
}

export default function ClientComplianceScorePanel({ clientId }) {
  const [pkt, setPkt] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [bump, setBump] = React.useState(0);

  const doAction = async (payload, okMsg) => {
    setBusy(true); setToast(null);
    try {
      const j = await apiPost('/api/compliance/consent-request', { client_id: clientId, ...payload });
      if (j?.ok) { setToast(j.dryRun ? `${okMsg} (dry-run)` : okMsg); setBump((b) => b + 1); }
      else setToast(j?.reason || j?.error || 'Action failed');
    } catch (e) { setToast(e.message); } finally { setBusy(false); setTimeout(() => setToast(null), 4000); }
  };

  React.useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiFetch(`/api/audit/packet?client_id=${encodeURIComponent(clientId)}`);
        if (cancelled) return;
        if (data?.error) throw new Error(data.error);
        setPkt(data);
      } catch (e) {
        setErr(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, bump]);

  if (loading) return <div className="text-xs text-slate-500 italic px-1 py-2">Loading compliance state…</div>;
  if (err)     return <div className="text-xs text-rose-700 italic px-1 py-2">Compliance packet failed: {err}</div>;
  if (!pkt)    return null;

  const results = CHECKS.map(c => ({ ...c, result: c.detect(pkt) }));
  const applicable = results.filter(r => !r.result.na);
  const doneCount = applicable.filter(r => r.result.done).length;
  const score = applicable.length ? Math.round((doneCount / applicable.length) * 100) : 100;
  const safe  = doneCount === applicable.length;

  const tone = score >= 90 ? 'emerald' : score >= 70 ? 'amber' : 'rose';
  const toneClass = tone === 'emerald' ? 'from-emerald-50 to-white border-emerald-300 text-emerald-900'
                : tone === 'amber'   ? 'from-amber-50 to-white border-amber-300 text-amber-900'
                :                       'from-rose-50 to-white border-rose-300 text-rose-900';

  const cl = pkt.client || {};
  const btn = 'text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition disabled:opacity-50';

  return (
    <div className="space-y-3">
      {/* ACA compliance packet — the definitive "OK to sign / enroll?" summary */}
      <AcaCompliancePacket clientId={clientId}/>

      {/* Consent + contact actions — send a consent request, record consent, or reach the client */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">Consent &amp; contact</div>
        <div className="flex flex-wrap gap-1.5">
          <button disabled={busy || !cl.phone} onClick={() => doAction({ channel: 'sms' }, 'Consent request texted.')} className={`${btn} border-emerald-300 text-emerald-800 hover:bg-emerald-50`} title={cl.phone ? '' : 'No phone on file'}>Request consent · Text</button>
          <button disabled={busy || !cl.email} onClick={() => doAction({ channel: 'email' }, 'Consent request emailed.')} className={`${btn} border-sky-300 text-sky-800 hover:bg-sky-50`} title={cl.email ? '' : 'No email on file'}>Request consent · Email</button>
          <button disabled={busy} onClick={() => doAction({ action: 'record_consent' }, 'Consent recorded.')} className={`${btn} border-amber-300 text-amber-800 hover:bg-amber-50`} title="Mark that the client gave consent (verbal/in person)">Mark consent captured</button>
          {cl.phone && <a href={`tel:${cl.phone}`} className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}>Call</a>}
          {cl.phone && <a href={`sms:${cl.phone}`} className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}>Text</a>}
          {cl.email && <a href={`mailto:${cl.email}`} className={`${btn} border-slate-300 text-slate-700 hover:bg-slate-50`}>Email</a>}
        </div>
        {toast && <div className="text-[11px] text-emerald-700 mt-1.5">{toast}</div>}
      </div>

      {/* Score header + safe-to-submit chip */}
      <div className={`flex items-center justify-between border-2 rounded-xl px-4 py-3 bg-gradient-to-b ${toneClass}`}>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Compliance score</div>
          <div className="text-3xl font-black tabular-nums">{score}<span className="text-lg opacity-60">/100</span></div>
          <div className="text-[11px] mt-0.5 opacity-80">{doneCount} of {applicable.length} required items complete</div>
        </div>
        <div className="text-right">
          {safe ? (
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-emerald-600 text-white">
              ✓ Safe to submit
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-rose-600 text-white">
              ⚑ Block submission
            </div>
          )}
          <div className="text-[10px] mt-1 opacity-60">
            {safe ? 'All blueprint items met' : `${applicable.length - doneCount} missing`}
          </div>
        </div>
      </div>

      {/* Checkmark grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {results.map(r => {
          const done = r.result.done;
          const na   = r.result.na;
          return (
            <div key={r.key} className={`flex items-start gap-2 px-3 py-2 rounded-md border ${
              na ? 'border-slate-200 bg-slate-50 text-slate-500'
                 : done ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                        : 'border-rose-200 bg-rose-50 text-rose-900'
            }`}>
              <span className={`text-lg leading-none mt-0.5 ${na ? 'text-slate-400' : done ? 'text-emerald-600' : 'text-rose-600'}`}>
                {na ? '—' : done ? '✓' : '✕'}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold">{r.label}</div>
                <div className="text-[10.5px] opacity-70 mt-0.5">{r.result.detail}</div>
              </div>
            </div>
          );
        })}
      </div>

      {!safe && (
        <div className="text-[11.5px] text-slate-600 italic px-1">
          Blueprint principle: <span className="text-slate-900 font-semibold">nothing can be submitted until required compliance items are complete.</span> Resolve the items above (each row's Forms & Documents panel can capture what's missing) before enrolling.
        </div>
      )}
    </div>
  );
}
